import {
  COMPOSITION_GRID_SPACING_MAX_MM,
  COMPOSITION_GRID_SPACING_MIN_MM,
} from '../../composition/model';
import { compositionPageDimensionsMm } from '../../composition/geometry';
import { compositionLogoBoundsForPage, isCompositionLogoDataValid } from '../../composition/logo';
import { resizeCompositionLayout } from '../../composition/pageResize';
import { applyCompositionTemplate, COMPOSITION_TEMPLATES } from '../../composition/templates';
import { deriveCompositionLegend } from '../../composition/legend';
import {
  COMPOSITION_SCALE_DENOMINATOR_MAX,
  COMPOSITION_SCALE_DENOMINATOR_MIN,
  scaleDenominatorForZoom,
} from '../../composition/scale';
import { isCompositionDraftValid } from '../../composition/validation';
import { isCapabilityAvailable } from '../../licensing/entitlements';
import { ACTION_OK, actionError, type AppStoreActionContext } from './actionSupport';
import type { AppState } from './appState';

function composerAccessError(state: AppState): ReturnType<typeof actionError> | null {
  return isCapabilityAvailable(state.entitlement, 'composition.workspace')
    ? null
    : actionError('Composer authoring requires Placeio Maps Pro.');
}

export type CompositionActions = Pick<
  AppState,
  | 'updateCompositionPage'
  | 'applyCompositionTemplate'
  | 'updateCompositionMapFrameRadius'
  | 'updateCompositionMapFrameBounds'
  | 'updateCompositionMapFrameBorder'
  | 'updateCompositionCamera'
  | 'useEditorViewForComposition'
  | 'updateCompositionText'
  | 'updateCompositionLegend'
  | 'updateCompositionLogo'
  | 'updateCompositionInsetMap'
  | 'updateCompositionScaleBar'
  | 'updateCompositionNorthArrow'
  | 'updateCompositionSourcesAttribution'
  | 'selectCompositionElement'
  | 'setCompositionInspectorSection'
  | 'setCompositionGridVisible'
  | 'setCompositionGridSpacingMm'
  | 'setCompositionSnapEnabled'
  | 'setCompositionObjectSnapEnabled'
  | 'setCompositionPreviewPixelsPerMm'
>;

export function createCompositionActions({ get, set }: AppStoreActionContext): CompositionActions {
  return {
    applyCompositionTemplate: (template) => {
      const state = get();
      const accessError = composerAccessError(state);
      if (accessError) return accessError;
      if (!COMPOSITION_TEMPLATES.some(({ id }) => id === template))
        return actionError('The template is invalid.');
      const current = state.project.composition.legend;
      const composition = applyCompositionTemplate(
        state.project.composition,
        template,
        deriveCompositionLegend(
          state.project.sources,
          state.project.layers,
          current.excludedLayerIds,
          current.excludedEntryKeysByLayer,
          state.project.annotationLayers,
          current.hiddenGroupHeadingIds,
        ),
        state.project,
      );
      if (!isCompositionDraftValid(composition))
        return actionError('The template layout is invalid.');
      set({ project: { ...state.project, composition } });
      return ACTION_OK;
    },
    updateCompositionMapFrameRadius: (cornerRadiusMm) => {
      const state = get();
      const accessError = composerAccessError(state);
      if (accessError) return accessError;
      const composition = {
        ...state.project.composition,
        mapFrame: { ...state.project.composition.mapFrame, cornerRadiusMm },
      };
      if (!isCompositionDraftValid(composition))
        return actionError('The map-frame radius is invalid.');
      set({ project: { ...state.project, composition } });
      return ACTION_OK;
    },
    updateCompositionPage: (patch) => {
      const state = get();
      const accessError = composerAccessError(state);
      if (accessError) return accessError;
      const changesPhysicalSize =
        patch.preset !== undefined ||
        patch.orientation !== undefined ||
        patch.customWidthMm !== undefined ||
        patch.customHeightMm !== undefined;
      if (
        changesPhysicalSize &&
        !isCapabilityAvailable(state.entitlement, 'composition.customPageSize') &&
        (patch.preset === 'custom' || state.project.composition.page.preset === 'custom')
      ) {
        return actionError('Custom composition dimensions require Pro.');
      }
      const page = { ...state.project.composition.page, ...patch };
      const oldComposition = state.project.composition;
      if (!isCompositionDraftValid({ ...oldComposition, page })) {
        return actionError('The composition page settings are invalid.');
      }
      const oldDimensions = compositionPageDimensionsMm(oldComposition.page);
      const newDimensions = compositionPageDimensionsMm(page);
      const resized = resizeCompositionLayout(
        oldComposition,
        oldDimensions,
        newDimensions,
        oldComposition.page.scaleLayoutWithPage,
      );
      const composition = resized ? { ...resized, page } : null;
      if (!isCompositionDraftValid(composition)) {
        return actionError('The composition page settings are invalid.');
      }
      set({ project: { ...state.project, composition } });
      return ACTION_OK;
    },
    updateCompositionMapFrameBounds: (bounds) => {
      const state = get();
      const accessError = composerAccessError(state);
      if (accessError) return accessError;
      const composition = {
        ...state.project.composition,
        mapFrame: { ...state.project.composition.mapFrame, bounds },
      };
      if (!isCompositionDraftValid(composition)) {
        return actionError('The map-frame bounds are invalid.');
      }
      set({ project: { ...state.project, composition } });
      return ACTION_OK;
    },
    updateCompositionMapFrameBorder: (patch) => {
      const state = get();
      const accessError = composerAccessError(state);
      if (accessError) return accessError;
      const composition = {
        ...state.project.composition,
        mapFrame: {
          ...state.project.composition.mapFrame,
          border: { ...state.project.composition.mapFrame.border, ...patch },
        },
      };
      if (!isCompositionDraftValid(composition)) {
        return actionError('The map-frame border settings are invalid.');
      }
      set({ project: { ...state.project, composition } });
      return ACTION_OK;
    },
    updateCompositionCamera: (camera) => {
      const state = get();
      const accessError = composerAccessError(state);
      if (accessError) return accessError;
      const composition = {
        ...state.project.composition,
        mapFrame: { ...state.project.composition.mapFrame, camera },
      };
      if (!isCompositionDraftValid(composition)) {
        return actionError('The composition camera is invalid.');
      }
      set({ project: { ...state.project, composition } });
      return ACTION_OK;
    },
    useEditorViewForComposition: () => {
      const state = get();
      const accessError = composerAccessError(state);
      if (accessError) return accessError;
      const editorView = state.project.map.view;
      const scaleDenominator = scaleDenominatorForZoom(
        editorView.zoom,
        editorView.center[1],
        state.shell.compositionPreviewPixelsPerMm,
      );
      if (scaleDenominator === null) {
        return actionError('The editor view cannot be represented at this physical scale.');
      }
      return get().updateCompositionCamera({
        center: [...editorView.center],
        scaleDenominator: Math.min(
          COMPOSITION_SCALE_DENOMINATOR_MAX,
          Math.max(COMPOSITION_SCALE_DENOMINATOR_MIN, scaleDenominator),
        ),
        bearing: editorView.bearing,
        pitch: editorView.pitch,
      });
    },
    updateCompositionText: (element, patch) => {
      const state = get();
      const accessError = composerAccessError(state);
      if (accessError) return accessError;
      const composition = {
        ...state.project.composition,
        [element]: { ...state.project.composition[element], ...patch },
      };
      if (!isCompositionDraftValid(composition)) {
        return actionError(`The composition ${element} settings are invalid.`);
      }
      set({ project: { ...state.project, composition } });
      return ACTION_OK;
    },
    updateCompositionLegend: (patch) => {
      const state = get();
      const accessError = composerAccessError(state);
      if (accessError) return accessError;
      const composition = {
        ...state.project.composition,
        legend: { ...state.project.composition.legend, ...patch },
      };
      if (!isCompositionDraftValid(composition)) {
        return actionError('The composition legend settings are invalid.');
      }
      set({ project: { ...state.project, composition } });
      return ACTION_OK;
    },
    updateCompositionInsetMap: (insetMap) => {
      const state = get();
      const accessError = composerAccessError(state);
      if (accessError) return accessError;
      const composition = { ...state.project.composition, insetMap };
      if (!isCompositionDraftValid(composition))
        return actionError('The inset map settings are invalid.');
      set({
        project: { ...state.project, composition },
        shell: {
          ...state.shell,
          selectedCompositionElement: insetMap ? 'inset-map' : null,
          compositionInspectorSection: 'inset-map',
        },
      });
      return ACTION_OK;
    },
    updateCompositionLogo: (logo) => {
      const state = get();
      const accessError = composerAccessError(state);
      if (accessError) return accessError;
      const authoredLogo = logo
        ? {
            ...logo,
            bounds:
              logo.dataUrl === state.project.composition.logo?.dataUrl
                ? logo.bounds
                : compositionLogoBoundsForPage(
                    logo.bounds,
                    logo.aspectRatio,
                    compositionPageDimensionsMm(state.project.composition.page),
                  ),
          }
        : null;
      if (
        authoredLogo &&
        authoredLogo.dataUrl !== state.project.composition.logo?.dataUrl &&
        !isCompositionLogoDataValid(authoredLogo)
      ) {
        return actionError('The composition logo image is invalid.');
      }
      const composition = { ...state.project.composition, logo: authoredLogo };
      if (!isCompositionDraftValid(composition)) {
        return actionError('The composition logo is invalid.');
      }
      set({
        project: { ...state.project, composition },
        ...(authoredLogo === null && state.shell.selectedCompositionElement === 'logo'
          ? {
              shell: {
                ...state.shell,
                selectedCompositionElement: null,
              },
            }
          : {}),
      });
      return ACTION_OK;
    },
    updateCompositionScaleBar: (patch) => {
      const state = get();
      const accessError = composerAccessError(state);
      if (accessError) return accessError;
      const composition = {
        ...state.project.composition,
        scaleBar: { ...state.project.composition.scaleBar, ...patch },
      };
      if (!isCompositionDraftValid(composition)) {
        return actionError('The composition scale-bar settings are invalid.');
      }
      set({ project: { ...state.project, composition } });
      return ACTION_OK;
    },
    updateCompositionNorthArrow: (patch) => {
      const state = get();
      const accessError = composerAccessError(state);
      if (accessError) return accessError;
      const composition = {
        ...state.project.composition,
        northArrow: { ...state.project.composition.northArrow, ...patch },
      };
      if (!isCompositionDraftValid(composition)) {
        return actionError('The composition north-arrow settings are invalid.');
      }
      set({ project: { ...state.project, composition } });
      return ACTION_OK;
    },
    updateCompositionSourcesAttribution: (patch) => {
      const state = get();
      const accessError = composerAccessError(state);
      if (accessError) return accessError;
      const composition = {
        ...state.project.composition,
        sourcesAttribution: { ...state.project.composition.sourcesAttribution, ...patch },
      };
      if (!isCompositionDraftValid(composition)) {
        return actionError('The Sources & attribution settings are invalid.');
      }
      set({ project: { ...state.project, composition } });
      return ACTION_OK;
    },
    selectCompositionElement: (element) =>
      set((state) => ({
        shell: {
          ...state.shell,
          selectedCompositionElement: element,
          compositionInspectorSection:
            element === null ? state.shell.compositionInspectorSection : element,
        },
      })),
    setCompositionInspectorSection: (section) =>
      set((state) => ({ shell: { ...state.shell, compositionInspectorSection: section } })),
    setCompositionGridVisible: (visible) =>
      set((state) => ({ shell: { ...state.shell, compositionGridVisible: visible } })),
    setCompositionGridSpacingMm: (spacingMm) => {
      if (
        !Number.isFinite(spacingMm) ||
        spacingMm < COMPOSITION_GRID_SPACING_MIN_MM ||
        spacingMm > COMPOSITION_GRID_SPACING_MAX_MM
      )
        return;
      set((state) => ({ shell: { ...state.shell, compositionGridSpacingMm: spacingMm } }));
    },
    setCompositionObjectSnapEnabled: (enabled) =>
      set((state) => ({ shell: { ...state.shell, compositionObjectSnapEnabled: enabled } })),
    setCompositionSnapEnabled: (enabled) =>
      set((state) => ({ shell: { ...state.shell, compositionSnapEnabled: enabled } })),
    setCompositionPreviewPixelsPerMm: (pixelsPerMm) => {
      if (!Number.isFinite(pixelsPerMm) || pixelsPerMm <= 0) return;
      set((state) =>
        Math.abs(state.shell.compositionPreviewPixelsPerMm - pixelsPerMm) <= 1e-9
          ? state
          : { shell: { ...state.shell, compositionPreviewPixelsPerMm: pixelsPerMm } },
      );
    },
  };
}
